import { CardContent, CardFooter } from '@/app/common/components/Card'
import Pagination from '@/app/common/components/Pagination'
import PaymentStatusBadge from '@/app/common/components/student_payment_status_badge/payment_status_badge'
import { displayTotalAmountToBePaidByApplicant } from '@/app/common/payment.utils'
import { currencyFormatter } from '@/app/common/utilities'
import { UTCToUsersTimezone } from '@/app/common/utils/dates/dates.utils'
import { Button } from '@/common/components/Button.tsx'
import { AppRoutePaths } from '@/routing/route_paths'
import { Payment } from '@/schemas/payments.schema'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'

interface PaymentsTableProps {
  payments: Payment[]
  currentPage: number
  totalRecords: number
  recordsPerPage: number
  onPageChange: (page: number) => void
  onPerPageChange: (perPage: number) => void
}

const PaymentsTable = ({
  payments,
  currentPage,
  recordsPerPage,
  totalRecords,
  onPageChange,
  onPerPageChange,
}: PaymentsTableProps) => {
  const { t: translate } = useTranslation()
  const navigate = useNavigate()
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const [showLeft, setShowLeft] = useState(false)
  const [showRight, setShowRight] = useState(true)

  const columnHelper = createColumnHelper<Payment>()

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    const updateShadows = () => {
      if (!el) return
      setShowLeft(el.scrollLeft > 5)
      setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 5)
    }

    updateShadows()
    el.addEventListener('scroll', updateShadows)
    window.addEventListener('resize', updateShadows)

    return () => {
      el.removeEventListener('scroll', updateShadows)
      window.removeEventListener('resize', updateShadows)
    }
  }, [])

  const columns = [
    columnHelper.accessor('payment_id', {
      id: 'transaction_no',
      header: () => (
        <>
          <span className="hidden sm:block">{translate('ID')}</span>
          <span className="block sm:hidden">{translate('ID')}</span>
        </>
      ),
      cell: (info) => {
        const row = info.row.original
        console.log(row.student_name)
        return (
          <div className="">
            <div className="">
              <div className="font-medium text-gray-900 flex md:block justify-between items-center">
                <span>{row.payment_id}</span>
              </div>
              <div className="mt-2 space-y-1 text-gray-500 font-normal md:hidden">
                <div>{row.student_name}</div>
                <div>{row.transaction_type}</div>
                <div>{row.provider_name}</div>
                <div>
                  {row.expense_categories.map((item) => item.name).join(',')}{' '}
                </div>
              </div>
            </div>
          </div>
        )
      },
    }),
    columnHelper.accessor('student_name', {
      id: 'student_name',
      header: () => <>{translate('student_name')}</>,
      cell: (info) => {
        const value = info.getValue()
        const isArray = value && Array.isArray(value)
        return (
          <div className="max-w-[7rem] overflow-x-hidden">
            {isArray ? value.join(', ') : value}
          </div>
        )
      },
    }),

    columnHelper.accessor('type', {
      id: 'type',
      header: () => <>{translate('payments_table.type')}</>,
      cell: (info) => {
        return <div>{info.getValue()}</div>
      },
    }),
    columnHelper.accessor('provider_name', {
      id: 'provider',
      header: () => <>{translate('provider')}</>,
      cell: (info) => (
        <div className="max-w-[7rem] truncate">{info.getValue()}</div>
      ),
    }),
    columnHelper.accessor((row) => row.expense_categories, {
      id: 'Category',
      cell: (info) => {
        console.log('Categories for row:', info.getValue())
        return (
          <div className="max-w-[7rem] truncate">
            {info
              .getValue()
              .map((category) => {
                return category.name
              })
              .join(', ')}
          </div>
        )
      },
    }),
    columnHelper.accessor('invoice_quote', {
      id: 'invoice_no',
      header: () => <>{translate('invoice_no')}</>,
      cell: (info) => (
        <div className="truncate max-w-[7rem]">{info.getValue()}</div>
      ),
    }),
    columnHelper.accessor(
      (row) => {
        return row
      },
      {
        id: 'total_amount',
        header: () => <>{translate('total_amount')}</>,
        cell: (info) => {
          const {
            total_amount,
            amount_provider,
            service_fee,
            type,
            service_fee_payer,
          } = info.getValue()

          const totalToBePaidByApplicant =
            displayTotalAmountToBePaidByApplicant(
              {
                base_amount: amount_provider,
                net_amount: total_amount,
                service_fee,
                serviceFeePayer: service_fee_payer,
              },
              type
            )

          return <div>{currencyFormatter.format(totalToBePaidByApplicant)}</div>
        },
      }
    ),
    columnHelper.accessor('comments', {
      id: 'comments',
      header: () => <>{translate('comments')}</>,
      cell: (info) => (
        <div className="max-w-[7rem] truncate">{info.getValue()}</div>
      ),
    }),
    columnHelper.accessor(
      (row) => {
        return {
          status: row.status,
          type: row.type,
        }
      },
      {
        id: 'status',
        header: () => <>{translate('status')}</>,
        cell: (info) => {
          const { status, type } = info.getValue()
          return (
            <div className="">
              <PaymentStatusBadge paymentType={type} paymentStatus={status} />
            </div>
          )
        },
      }
    ),
    columnHelper.accessor('created_datetime', {
      id: 'created_at',
      header: () => <>{translate('payments_table.created_at')}</>,
      cell: (info) => {
        console.log(info.getValue())
        return <div className="">{UTCToUsersTimezone(info.getValue())}</div>
      },
    }),
    columnHelper.display({
      id: 'action',
      header: () => <span className="pr-2 2xl:p-1">{translate('action')}</span>,
      cell: (info) => {
        const row = info.row.original
        return (
          <Button 
            onClick={() => {
              navigate(
                AppRoutePaths.payments.details.index.replace(
                  ':paymentId',
                  row.payment_id.toString()
                )
              )
            }}
          >
            {translate('payments_table.details')}
          </Button>
        )
      },
    }),
  ]

  const table = useReactTable({
    data: payments,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: 1,
  })

  return (
    <div className="relative">
      <div className="h-[55dvh] overflow-hidden">
        <div
          ref={scrollRef}
          className={`scroll-shadow h-full overflow-auto w-full ${
            showLeft ? 'show-left' : ''
          } ${showRight ? 'show-right' : ''}`}
        >
          <CardContent className="p-0 border-b border-transparent">
            <div className="min-w-[1000px] inline-block align-middle">
              <table className="w-full border-collapse">
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          className="px-4 py-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap"
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row) => (
                    <tr key={row.id} className="even:bg-gray-50">
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className="py-3 px-4 text-sm text-gray-700 whitespace-nowrap border-t border-gray-200"
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </div>
      </div>
      <CardFooter className="p-0 border-t border-transparent mt-3">
        <Pagination
          itemsPerPage={recordsPerPage}
          currentPage={currentPage}
          totalRows={totalRecords}
          onPageChange={onPageChange}
          onPerPageChange={onPerPageChange}
        />
      </CardFooter>
    </div>
  )
}

export default PaymentsTable
